// Client-side PDF dossier generator. Uses a static import so the jsPDF
// bundle ships with the main chunk — previously we dynamic-import()'d
// it, but Cloudflare Pages occasionally served stale HTML pointing at a
// removed chunk, producing "'text/html' is not a valid JavaScript MIME
// type" at click time. Static import removes that failure mode.

import { jsPDF } from "jspdf";
import type { Case, CaseRecord } from "../types";
import { SOURCE_TYPE_LABELS } from "../types";

interface CaseDossier {
  case: Case;
  records: CaseRecord[];
}

const MARGIN = 48;
const LINE_HEIGHT = 14;

// UTC-anchored so a record stored as "2025-12-18T00:00:00Z" prints
// as Dec 18 regardless of the reader's timezone.
import { formatDateTime as _formatDateTime } from "./formatDate";

function formatDate(iso: string): string {
  return _formatDateTime(iso) || iso;
}

export async function exportCaseAsPdf({
  case: caseData,
  records,
}: CaseDossier): Promise<Blob> {
  const doc = new jsPDF({
    unit: "pt",
    format: "a4",
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - MARGIN * 2;

  let y = MARGIN;

  function advance(lines = 1) {
    y += LINE_HEIGHT * lines;
    if (y > pageHeight - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  }

  function writeLine(text: string, opts: { size?: number; bold?: boolean; color?: [number, number, number] } = {}) {
    doc.setFontSize(opts.size ?? 10);
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    if (opts.color) doc.setTextColor(opts.color[0], opts.color[1], opts.color[2]);
    else doc.setTextColor(20, 20, 20);
    const wrapped = doc.splitTextToSize(text, maxWidth);
    for (const line of wrapped) {
      if (y > pageHeight - MARGIN) {
        doc.addPage();
        y = MARGIN;
      }
      doc.text(line, MARGIN, y);
      y += LINE_HEIGHT;
    }
  }

  // Cover page.
  writeLine(caseData.name, { size: 22, bold: true });
  advance(0.5);
  if (caseData.description) {
    writeLine(caseData.description, { size: 11, color: [90, 75, 55] });
  }
  advance(0.5);
  writeLine(
    `Forwarding address: ${caseData.forward_address}`,
    { size: 9, color: [130, 110, 80] },
  );
  writeLine(`Case created: ${formatDate(caseData.created_at)}`, {
    size: 9,
    color: [130, 110, 80],
  });
  writeLine(
    `Records on file: ${records.length} — generated ${formatDate(new Date().toISOString())}`,
    { size: 9, color: [130, 110, 80] },
  );
  advance(1);

  // Records, sorted chronologically (oldest first) to read as a narrative.
  const sorted = [...records].sort((a, b) =>
    a.record_date < b.record_date
      ? -1
      : a.record_date > b.record_date
        ? 1
        : 0,
  );

  for (const r of sorted) {
    // Each record starts on a new page if it's close to the margin — keeps
    // the record header from stranding at the bottom of a page.
    if (y > pageHeight - MARGIN - LINE_HEIGHT * 6) {
      doc.addPage();
      y = MARGIN;
    }

    // Separator.
    doc.setDrawColor(200, 180, 140);
    doc.line(MARGIN, y - 4, pageWidth - MARGIN, y - 4);
    advance(0.5);

    const label =
      `${r.direction === "inbound" ? "↓ In" : "↑ Out"} · ${SOURCE_TYPE_LABELS[r.source_type] ?? r.source_type}`;
    writeLine(label, { size: 10, bold: true, color: [150, 110, 60] });

    if (r.subject) writeLine(r.subject, { size: 13, bold: true });

    const header: string[] = [];
    header.push(`Date: ${formatDate(r.record_date)}`);
    if (r.from_addr) header.push(`From: ${r.from_addr}`);
    if (r.to_addr) header.push(`To: ${r.to_addr}`);
    if (r.forwarded_via) header.push(`Via: ${r.forwarded_via}`);
    if (r.file_name)
      header.push(`Attachment: ${r.file_name} (${r.file_mime_type ?? "?"})`);
    for (const h of header) writeLine(h, { size: 9, color: [110, 95, 70] });

    advance(0.3);

    const body = r.body_text ?? r.ocr_text ?? "(no content)";
    writeLine(body, { size: 10 });

    if (r.notes) {
      advance(0.3);
      writeLine(`Notes: ${r.notes}`, { size: 10, color: [140, 100, 60] });
    }

    advance(1);
  }

  return doc.output("blob");
}
